import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Icon } from "@iconify/react";
import FeedbackWidget from "./FeedbackWidget";

/** Public URL for Resource center pill icon (see public/resource_center.svg) */
const RESOURCE_CENTER_SVG = `${process.env.PUBLIC_URL || ""}/resource_center.svg`;

// Figma: radial gradient + shadow (TPX support FAB)
const FAB_GRADIENT =
  "radial-gradient(94.56% 121.79% at 5.44% 100%, #A6E03B 0%, #6ECA69 47.92%, #56BFBB 100%)";
const FAB_SHADOW = "0 4px 4px 0 rgba(0, 0, 0, 0.25)";

/** Default / hover sizes (Figma): 36×36 at rest, 52×52 on hover and when menu is open */
const FAB_SIZE_COLLAPSED = 36;
const FAB_SIZE_EXPANDED = 52;
const FAB_ICON_COLLAPSED = 18;
const FAB_ICON_EXPANDED = 24;

const MENU_ITEMS = [
  {
    id: "feedback",
    label: "Feedback",
    iconifyIcon: "solar:document-add-linear",
    action: "feedback",
  },
  process.env.REACT_APP_ENV !== "prod" && {
    id: "chat",
    label: "Chat with us",
    iconifyIcon: "solar:chat-round-line-linear",
    action: "chat",
  },
  {
    id: "resource",
    label: "Resource center",
    imageSrc: RESOURCE_CENTER_SVG,
    action: "resource",
  },
].filter(Boolean);

const STAGGER_MS = 80;

/** White menu rows: 36px tall always — not FAB_SIZE_EXPANDED (52) or circles read as the main FAB. */
const MENU_CIRCLE_SIZE = 36;
/** Pills with labels use same row height as icon-only circles */
const MENU_PILL_HEIGHT = 36;
const MENU_ICON_CIRCLE = 16;
const MENU_ICON_PILL = 14;
/**
 * After menu opens, hold expandedIndex at -1 this long so all three circles render first,
 * then labels expand bottom → top (Feedback → Chat → Resource).
 */
const CIRCLE_ONLY_MS = 220;
/** Extra delay after circle phase before first label (Feedback) */
const LABEL_STAGGER_BASE_MS = 120;

function getJsdIframe() {
  return (
    document.getElementById("jsd-widget") ||
    document.querySelector('iframe[src*="jsd-widget.atlassian.com"]')
  );
}

/** Un-hide JSD iframe (CSS in index.html hides default launcher). */
function unhideJsdIframe(iframe) {
  if (!iframe) return;
  iframe.style.setProperty("display", "block");
  iframe.style.setProperty("visibility", "visible");
  iframe.style.setProperty("pointer-events", "auto");
}

/** Make iframe renderable (display:block) but invisible — allows the widget to process API calls without the user seeing the launcher. */
function prerenderJsdIframe(iframe) {
  if (!iframe) return;
  iframe.style.setProperty("display", "block");
  iframe.style.setProperty("visibility", "hidden");
  iframe.style.setProperty("pointer-events", "none");
}

/**
 * Hide the default launcher (#help-button) inside the JSD iframe.
 * Wrapped in try/catch because the iframe may be cross-origin in some environments.
 */
function hideJsdLauncherButton(iframe) {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return;
    const STYLE_ID = "tpx-hide-launcher";
    if (!doc.getElementById(STYLE_ID)) {
      const style = doc.createElement("style");
      style.id = STYLE_ID;
      style.textContent =
        "#help-button, #button-container { display: none !important; visibility: hidden !important; }";
      doc.head.appendChild(style);
    }
  } catch (_) { /* cross-origin — rely on two-phase visibility instead */ }
}

/** True when the Pendo Resource Center panel is visible on screen. */
function isPendoRcVisible() {
  const rc = document.getElementById("pendo-resource-center-container");
  return !!(rc && rc.offsetHeight > 0);
}

/**
 * Open the Pendo Resource Center — only if it is not already visible.
 * Prefers showLauncher (one-way open, no toggle state) over toggleLauncher.
 */
function tryOpenPendoResourceCenter() {
  try {
    const p = typeof window !== "undefined" ? window.pendo : null;
    if (!p) return false;
    if (isPendoRcVisible()) return true;
    if (typeof p.showLauncher === "function") {
      p.showLauncher();
      return true;
    }
    if (typeof p.toggleLauncher === "function") {
      p.toggleLauncher();
      return true;
    }
  } catch (e) { /* ignore */ }
  return false;
}

const RC_POSITION_STYLE_ID = "tpx-rc-position-override";

function injectRcPositionStyle() {
  if (document.getElementById(RC_POSITION_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = RC_POSITION_STYLE_ID;
  style.textContent = `
    #pendo-resource-center-container {
      inset: auto !important;
      position: fixed !important;
      bottom: 8px !important;
      right: 8px !important;
    }
  `;
  document.head.appendChild(style);
}

function removeRcPositionStyle() {
  document.getElementById(RC_POSITION_STYLE_ID)?.remove();
}

function closePendoRcViaButton() {
  const btn = document.querySelector("._pendo-resource-center-close-button");
  if (btn) { btn.click(); return true; }
  return false;
}

/**
 * JSD / Trudy — open help hub (Resource Center style panel). Embeds differ; try several APIs / postMessage.
 */
function openJsdResourceCenter() {
  const iframe = getJsdIframe();
  if (!iframe) return false;
  unhideJsdIframe(iframe);

  const run = () => {
    try {
      const w = window.JSDWidget;
      if (w && typeof w.openResourceCenter === "function") {
        w.openResourceCenter();
        return;
      }
      if (w && typeof w.open === "function") {
        try {
          w.open("resourceCenter");
          return;
        } catch (e1) {
          try {
            w.open({ view: "resourceCenter" });
            return;
          } catch (e2) {
            w.open();
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
    try {
      iframe.contentWindow?.postMessage?.({ type: "jsd-open", view: "resourceCenter" }, "*");
      iframe.contentWindow?.postMessage?.({ type: "open-resource-center" }, "*");
    } catch (e) {
      /* ignore */
    }
    try {
      const doc = iframe.contentDocument;
      if (doc) {
        const els = doc.querySelectorAll("button, a, [role='tab'], [role='button']");
        for (const el of els) {
          const t = (el.textContent || "").trim().toLowerCase();
          if (t.includes("resource") && t.includes("center")) {
            el.click();
            break;
          }
        }
      }
    } catch (e) {
      /* cross-origin */
    }
  };

  run();
  setTimeout(run, 250);
  setTimeout(run, 700);
  return true;
}

/**
 * Prefer Pendo Resource Center when configured; otherwise Atlassian JSD hub.
 * @returns {boolean} true if a path ran (Pendo API invoked or JSD iframe found).
 */
function openResourceCenter() {
  if (tryOpenPendoResourceCenter()) return true;
  if (!getJsdIframe()) return false;
  openJsdResourceCenter();
  return true;
}

let _chatObserver = null;
let _chatRehideInterval = null;

function cleanupChatWatchers() {
  if (_chatObserver) { _chatObserver.disconnect(); _chatObserver = null; }
  if (_chatRehideInterval) { clearInterval(_chatRehideInterval); _chatRehideInterval = null; }
}

/**
 * Open Trudy chat (JSD widget) with a two-phase visibility approach:
 *   Phase 1 — display:block + visibility:hidden so the widget can render and respond to open().
 *   Phase 2 — After a delay for the panel to expand, set visibility:visible.
 * The launcher (#help-button) is also hidden inside the iframe for a belt-and-suspenders guarantee.
 */
function openTrudy() {
  const iframe = getJsdIframe();
  if (!iframe) return false;

  cleanupChatWatchers();

  // Phase 1: renderable but invisible — widget processes API calls without the user seeing the launcher
  prerenderJsdIframe(iframe);
  hideJsdLauncherButton(iframe);

  let hasBeenExpanded = false;
  function rehideIfClosed() {
    const h = parseFloat(iframe.style.height) || 0;
    if (h >= 200) hasBeenExpanded = true;
    if (hasBeenExpanded && h > 0 && h < 150) {
      iframe.style.setProperty("display", "none");
      iframe.style.setProperty("visibility", "hidden");
      iframe.style.setProperty("pointer-events", "none");
      cleanupChatWatchers();
    }
  }
  function startWatchingForClose() {
    try {
      _chatObserver = new MutationObserver(rehideIfClosed);
      _chatObserver.observe(iframe, { attributes: true, attributeFilter: ["style"] });
    } catch (e) {}
    _chatRehideInterval = setInterval(rehideIfClosed, 500);
    setTimeout(() => {
      if (_chatRehideInterval) { clearInterval(_chatRehideInterval); _chatRehideInterval = null; }
    }, 300000);
  }
  setTimeout(startWatchingForClose, 2000);

  // Phase 2: trigger open, then reveal after the panel has had time to expand
  let opened = false;
  function tryOpen() {
    if (opened) return;
    try {
      if (typeof window.JSDWidget !== "undefined" && typeof window.JSDWidget.open === "function") {
        window.JSDWidget.open();
        opened = true;
        return;
      }
      iframe.contentWindow?.postMessage?.({ type: "jsd-open", action: "open" }, "*");
      iframe.contentWindow?.postMessage?.({ type: "open" }, "*");
      const doc = iframe.contentDocument;
      if (doc) {
        const btn = doc.querySelector("#help-button") || doc.querySelector("[class*='help-button']") || doc.querySelector("button");
        if (btn) { btn.click(); opened = true; }
      }
    } catch (e) {}
  }
  tryOpen();
  setTimeout(tryOpen, 500);

  // Phase 3: reveal the iframe once the panel should be expanded
  setTimeout(() => {
    hideJsdLauncherButton(iframe);
    unhideJsdIframe(iframe);
  }, 600);

  return true;
}

const SupportFAB = () => {
  const [open, setOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [showFeedbackWidget, setShowFeedbackWidget] = useState(false);
  /** Blocks FAB clicks while menu closes — avoids mouseup on FAB after pill mousedown opening/toggling wrongly. */
  const [fabPointerBlocked, setFabPointerBlocked] = useState(false);
  const menuAnimationTimersRef = useRef([]);
  const lastExternalWidgetRef = useRef(null);
  const fabRootRef = useRef(null);
  const closeMenuWithAnimationRef = useRef(() => {});

  const clearMenuTimers = () => {
    menuAnimationTimersRef.current.forEach(clearTimeout);
    menuAnimationTimersRef.current = [];
  };

  /**
   * Reverse of open: labels collapse top → bottom (Resource → Chat → Feedback), then circles, then menu hides.
   */
  const closeMenuWithAnimation = (onFullyClosed) => {
    clearMenuTimers();
    if (!open) {
      setExpandedIndex(-1);
      setFabPointerBlocked(false);
      onFullyClosed?.();
      return;
    }
    const e = expandedIndex;
    if (e < 0) {
      setOpen(false);
      setExpandedIndex(-1);
      setFabPointerBlocked(false);
      onFullyClosed?.();
      return;
    }
    setFabPointerBlocked(true);
    for (let n = e; n >= 0; n--) {
      const delay = (e - n) * STAGGER_MS;
      const id = setTimeout(() => {
        setExpandedIndex(n - 1);
      }, delay);
      menuAnimationTimersRef.current.push(id);
    }
    const finalDelay = e * STAGGER_MS + CIRCLE_ONLY_MS;
    const idDone = setTimeout(() => {
      setOpen(false);
      setExpandedIndex(-1);
      setFabPointerBlocked(false);
      onFullyClosed?.();
    }, finalDelay);
    menuAnimationTimersRef.current.push(idDone);
  };

  closeMenuWithAnimationRef.current = closeMenuWithAnimation;

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      const t = event.target;
      if (!(t instanceof Node)) return;
      if (fabRootRef.current?.contains(t)) return;
      const rc = document.getElementById("pendo-resource-center-container");
      if (rc?.contains(t)) return;
      if (isPendoRcVisible()) return;
      closeMenuWithAnimationRef.current();
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const scheduleOpenAnimation = () => {
    clearMenuTimers();
    setOpen(true);
    setExpandedIndex(-1);
    MENU_ITEMS.forEach((_, i) => {
      const delay =
        CIRCLE_ONLY_MS +
        LABEL_STAGGER_BASE_MS +
        (i + 1) * STAGGER_MS;
      const id = setTimeout(() => {
        setExpandedIndex((prev) => Math.max(prev, i));
      }, delay);
      menuAnimationTimersRef.current.push(id);
    });
  };

  const toggleOpen = (e) => {
    e?.stopPropagation?.();

    if (!open) {
      closePendoRcViaButton();
      injectRcPositionStyle();
    }

    if (lastExternalWidgetRef.current === "chat") {
      cleanupChatWatchers();
      try { window.JSDWidget?.close?.(); } catch (e) {}
      const iframe = getJsdIframe();
      if (iframe) {
        iframe.style.setProperty("display", "none");
        iframe.style.setProperty("visibility", "hidden");
        iframe.style.setProperty("pointer-events", "none");
      }
      lastExternalWidgetRef.current = null;
    }

    if (open) {
      closeMenuWithAnimation();
    } else {
      scheduleOpenAnimation();
    }
  };

  const handleAction = (e, action) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (action === "feedback") {
      closeMenuWithAnimation(() => setShowFeedbackWidget(true));
      return;
    }
    if (action === "chat") {
      closeMenuWithAnimation(() => {
        if (!openTrudy()) {
          let attempts = 0;
          const id = setInterval(() => {
            attempts++;
            if (openTrudy() || attempts >= 20) clearInterval(id);
          }, 150);
        }
        lastExternalWidgetRef.current = "chat";
      });
      return;
    }
    if (action === "resource") {
      const fab = document.querySelector(".tpx-support-fab");
      if (fab) fab.style.setProperty("z-index", "1");
      injectRcPositionStyle();
      openResourceCenter();
      let rcSeen = false;
      let goneCount = 0;
      const pollId = setInterval(() => {
        if (isPendoRcVisible()) {
          rcSeen = true;
          goneCount = 0;
        } else if (rcSeen) {
          goneCount++;
          if (goneCount >= 3) {
            clearInterval(pollId);
            removeRcPositionStyle();
            if (fab) fab.style.removeProperty("z-index");
            setOpen(false);
            setExpandedIndex(-1);
          }
        }
      }, 500);
      return;
    }
    closeMenuWithAnimation();
  };

  return (
    <>
      <Box
        ref={fabRootRef}
        className="tpx-support-fab"
        sx={{
          position: "fixed",
          bottom: 8,
          right: 8,
          zIndex: 2147483647,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0,
        }}
      >
        {/* Menu items: top to bottom in DOM = Resource center, Chat with us, Feedback */}
        {[...MENU_ITEMS].reverse().map((item, reversedIndex) => {
          const index = MENU_ITEMS.length - 1 - reversedIndex;
          /** Label visible = Figma steps 2–4 for this row (bottom item first). */
          const showLabel = expandedIndex >= index && open;
          const iconSize = showLabel ? MENU_ICON_PILL : MENU_ICON_CIRCLE;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={(e) => handleAction(e, item.action)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                cursor: "pointer",
                border: "none",
                padding: 0,
                background: "transparent",
                font: "inherit",
                position: "relative",
                zIndex: 2147483647,
                ...(open
                  ? {
                      marginBottom: "8px",
                      minHeight: showLabel ? MENU_PILL_HEIGHT : MENU_CIRCLE_SIZE,
                      height: "auto",
                      opacity: 1,
                      visibility: "visible",
                      pointerEvents: "auto",
                      transform: "translateY(0)",
                      transition:
                        "opacity 0.2s ease, transform 0.25s ease, min-height 0.3s ease",
                      transitionDelay: `${index * 40}ms`,
                    }
                  : {
                      marginBottom: 0,
                      height: 0,
                      minHeight: 0,
                      overflow: "hidden",
                      opacity: 0,
                      visibility: "hidden",
                      pointerEvents: "none",
                      transform: "translateY(8px)",
                    }),
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: showLabel ? MENU_PILL_HEIGHT : MENU_CIRCLE_SIZE,
                  minWidth: showLabel ? "auto" : MENU_CIRCLE_SIZE,
                  maxWidth: showLabel ? 240 : MENU_CIRCLE_SIZE,
                  overflow: "hidden",
                  background: "#FFF",
                  borderRadius: "50px",
                  boxShadow: FAB_SHADOW,
                  transition:
                    "max-width 0.3s ease, min-width 0.3s ease, height 0.3s ease",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 36,
                    height: 36,
                    minWidth: 36,
                    minHeight: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#212B36",
                    flexShrink: 0,
                    transition: "width 0.3s ease, height 0.3s ease, min-width 0.3s ease",
                  }}
                >
                  {item.imageSrc ? (
                    <Box
                      component="img"
                      src={item.imageSrc}
                      alt=""
                      sx={{
                        width: iconSize,
                        height: iconSize,
                        objectFit: "contain",
                        display: "block",
                        transition: "width 0.3s ease, height 0.3s ease",
                      }}
                    />
                  ) : item.iconifyIcon ? (
                    <Icon
                      icon={item.iconifyIcon}
                      width={iconSize}
                      height={iconSize}
                      style={{ color: "#212B36" }}
                    />
                  ) : null}
                </Box>
                {showLabel && (
                  <Typography
                    component="span"
                    sx={{
                      pr: 1.5,
                      pl: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: '"Public Sans", sans-serif',
                      color: "#212B36",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

          {/* Main FAB: 36×36 default → 52×52 on hover / when menu open (Figma) */}
        <IconButton
          onClick={toggleOpen}
          aria-label={open ? "Close support menu" : "Open support menu"}
          className="tpx-support-fab-trigger"
          sx={{
            p: 0,
            boxSizing: "border-box",
            pointerEvents: fabPointerBlocked ? "none" : "auto",
            width: open ? FAB_SIZE_EXPANDED : FAB_SIZE_COLLAPSED,
            height: open ? FAB_SIZE_EXPANDED : FAB_SIZE_COLLAPSED,
            minWidth: open ? FAB_SIZE_EXPANDED : FAB_SIZE_COLLAPSED,
            transition: "width 0.2s ease, height 0.2s ease, min-width 0.2s ease",
            background: FAB_GRADIENT,
            borderRadius: "50px",
            boxShadow: FAB_SHADOW,
            color: "#fff",
            // Hover: grow FAB; do not use filter — it shifts the + away from #FFFFFF
            "&:hover": {
              width: FAB_SIZE_EXPANDED,
              height: FAB_SIZE_EXPANDED,
              minWidth: FAB_SIZE_EXPANDED,
              background: FAB_GRADIENT,
              boxShadow: "0 6px 12px 0 rgba(0, 0, 0, 0.22)",
            },
            "& .fab-add-icon": {
              fontSize: FAB_ICON_COLLAPSED,
              color: "#FFFFFF",
              transition:
                "transform 0.25s ease, font-size 0.2s ease",
            },
            "&:hover .fab-add-icon": {
              fontSize: FAB_ICON_EXPANDED,
              color: "#FFFFFF",
            },
            ...(open && {
              width: FAB_SIZE_EXPANDED,
              height: FAB_SIZE_EXPANDED,
              minWidth: FAB_SIZE_EXPANDED,
              "& .fab-add-icon": {
                fontSize: FAB_ICON_EXPANDED,
                color: "#FFFFFF",
              },
            }),
          }}
        >
          {open ? (
            <CloseRounded className="fab-add-icon" sx={{ color: "#FFFFFF" }} />
          ) : (
            <Box component="span" className="fab-add-icon"
              sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "inherit", lineHeight: 1 }}>
              ?
            </Box>
          )}
        </IconButton>
      </Box>

      {showFeedbackWidget && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 20,
            zIndex: 2147483647,
          }}
        >
          <FeedbackWidget
            isOpen={showFeedbackWidget}
            setShowFeedbackWidget={setShowFeedbackWidget}
          />
        </Box>
      )}
    </>
  );
};

export default SupportFAB;
